<template>
  <div>
    <EmptyPage
      v-if="isEmptyPage"
      :direction-text="directionText"
      :dates-text="datesText"
      :is-loading="products.isLoading.value || booking.isLoading.value"
      :base-price="priceBreakdown.basePrice"
      :total-price="priceBreakdown.totalPrice"
      :is-agreed="booking.isAgreed.value"
      :public-offer-bs-name="publicOfferBsName"
      :exchange-return-bs-name="exchangeReturnBsName"
      @onGoBack="onGoBack"
      @onAgree="booking.setAgreement"
      @onSubmit="onSubmit"
    />
    <FreePageLayout v-else>
      <template #header>
        <FreeHeader class="p-4" is-centered>
          <template #leftAction>
            <FreeBackButton @click="onGoBack" />
          </template>
          <template #default>
            <h2 class="text-center text-lg font-semibold">
              {{ $t('products.title') }}
            </h2>
          </template>
        </FreeHeader>
      </template>
      <template #default>
        <div
          v-if="products.isLoading.value || booking.isLoading.value"
          class="flex flex-grow items-center justify-center"
        >
          <FreeLoader />
        </div>

        <main v-else class="flex flex-grow flex-col pb-36">
          <AncilarriesSection
            :offer-id="offerId"
            :products="products"
            :seat-map-check-query="checkSeatMapQuery"
          />
          <div
            class="flex-grow"
            :class="
              products.visibleProducts.value?.length > 0
                ? 'space-y-8'
                : 'space-y-6'
            "
          >
            <PackageList
              v-if="products.packages.value.length > 0"
              :packages="products.packages.value"
              :package-info="products.packageInfo.value"
              @onSelect="onSelectPackage"
              @onUnselect="onUnselectPackage"
            />
            <div class="space-y-6">
              <div
                v-if="products.visibleProducts.value?.length > 0"
                class="space-y-3"
              >
                <h2 class="text-text-neutral-tertiary text-sm font-medium">
                  {{ $t('products.products.title') }}
                </h2>
                <ProductCard
                  v-for="product in products.visibleProducts.value"
                  :key="product.id"
                  :product="product"
                  :products-state="products.state.value"
                  :package-info="products.packageInfo.value"
                  @onProductAdd="products.addProductSeparately"
                  @onProductRemove="products.removeProduct"
                  @onProductDataUpdate="products.setProductData"
                />
              </div>
              <AgreementSection
                :public-offer-bs-name="publicOfferBsName"
                :exchange-return-bs-name="exchangeReturnBsName"
                :is-agreed="booking.isAgreed.value"
                @onAgree="booking.setAgreement"
              />
            </div>
          </div>
          <PriceAndButtonCard
            source-page="products"
            :price-amount="priceBreakdown.totalPrice"
            :button-title="$t('contacts.book')"
            @onButtonAction="onSubmit"
          >
            <PriceDetails class="-mt-5" :price-breakdown="priceBreakdown" />
          </PriceAndButtonCard>
        </main>
      </template>
    </FreePageLayout>
    <BookingModals
      @onWarningConfirm="onWarningConfirm"
      @onGoToPaymentPage="goToPaymentPage"
      @onCancelPrevBooking="onCancelPrevBooking"
      @onNewSearchStart="handleNewSearchStart"
    />
    <PublicOfferBottomSheet
      :name="publicOfferBsName"
      :fetch-public-offer="publicOfferApi.fetchPublicOffer"
    />
    <ExchangeRefundRulesBottomSheet
      :name="exchangeReturnBsName"
      :offer-id="offerId"
    />
    <FlightDelaySuccessModal
      :name="flightDelayConfig.FLIGHT_DELAY_SUCCESS_MODAL"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import {
    FreeHeader,
    FreePageLayout,
    FreeBackButton,
    FreeLoader,
    PublicOfferBottomSheet,
  } from 'freedom-shared';
  import { useI18n } from 'vue-i18n';
  import useVuelidate from '@vuelidate/core';

  import { ProductCard } from '@/widgets/product-card';
  import { ancillaryBaggageModel } from '@/widgets/ancillary-baggage';
  import { AgreementSection } from '@/widgets/booking-modals';
  import {
    flightDelayConfig,
    FlightDelaySuccessModal,
  } from '@/widgets/flight-delay';

  import { bookingStateModel } from '@/features/booking-state';
  import { PriceAndButtonCard } from '@/features/price-and-button-card';

  import { PackageList } from '@/entities/product-packages';
  import { publicOfferApi } from '@/entities/public-offer';
  import { ExchangeRefundRulesBottomSheet } from '@/entities/exchange-refund-rules-bottom-sheet';

  import {
    usePriceBreakDown,
    useProductsState,
    useBookingFlow,
    useProductsEmptyPageHeader,
    useProductsPageLoadingState,
    useSeatMapPricesFromBooking,
    useProductsPageBooking,
    useProductsPageEffects,
  } from './model';
  import PriceDetails from './PriceDetails.vue';
  import { AncilarriesSection, BookingModals, EmptyPage } from './ui';

  const publicOfferBsName = 'public-offer-bottom-sheet';
  const exchangeReturnBsName = 'exchange-return-rules-bottom-sheet';

  const props = defineProps<{
    offerId: string;
  }>();

  const bookingStore = bookingStateModel.useBookingStore();
  const ancillariesStore = ancillaryBaggageModel.useAncillariesStore();

  const { t } = useI18n();
  const vuelidate = useVuelidate();

  const { directionText, datesText } = useProductsEmptyPageHeader();

  const products = useProductsState({
    offerId: props.offerId,
    packageInfo: bookingStore.state.packageInfo,
    initialState: bookingStore.state.products.state,
    passengers: bookingStore.state.passengers,
    roamingProducts: bookingStore.state.roaming,
  });

  const { checkSeatMapQuery, isProductsLoading, isEmptyPage } =
    useProductsPageLoadingState(props.offerId, products, ancillariesStore);

  const { seatMapPrice } = useSeatMapPricesFromBooking();

  const priceBreakdown = usePriceBreakDown(
    bookingStore,
    products,
    ancillariesStore,
    seatMapPrice,
  );

  const { booking, modal } = useProductsPageBooking();

  const {
    onSubmit,
    goToPaymentPage,
    onCancelPrevBooking,
    onWarningConfirm,
    updateProductsStateAtBookingStore,
  } = useBookingFlow({
    offerId: props.offerId,
    products,
    booking,
    bookingStore,
    modal,
    vuelidate,
    ancillariesStore,
  });

  const { onGoBack, onSelectPackage, onUnselectPackage, handleNewSearchStart } =
    useProductsPageEffects({
      offerId: props.offerId,
      products,
      booking,
      bookingStore,
      modal,
      ancillariesStore,
      checkSeatMapQuery,
      isProductsLoading,
      t,
      updateProductsStateAtBookingStore,
    });
</script>
